import React, { useState } from 'react';
import { useLocalization } from '../contexts/LocalizationContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  Settings as SettingsIcon,
  Globe,
  Bell,
  Shield,
  Database,
  Palette,
  Clock,
  Mail,
  Smartphone,
  Key,
  AlertTriangle,
  Save,
  RefreshCw,
  Download,
  Lock,
  CheckCircle2,
  Server,
  Monitor,
} from 'lucide-react';

const Settings: React.FC = () => {
  const { language, country, countryConfig, setLanguage, setCountry, t, getAvailableCountries } = useLocalization();
  const { user } = useAuth();
  const { theme, setTheme: setAppTheme } = useTheme();

  const [activeSection, setActiveSection] = useState('general');
  const [saved, setSaved] = useState(false);
  const [settings, setSettings] = useState({
    theme: theme,
    language: language,
    country: country,
    timezone: countryConfig.timezone,
    dateFormat: 'DD/MM/YYYY',
    numberFormat: 'european',
    emailNotifications: true,
    smsNotifications: true,
    pushNotifications: true,
    transactionAlerts: true,
    balanceAlerts: true,
    securityAlerts: true,
    marketingEmails: false,
    sessionTimeout: 30,
    twoFactorAuth: true,
    loginAlerts: true,
    ipWhitelist: true,
    autoLogout: true,
    autoBackup: true,
    backupFrequency: 'daily',
    dataRetention: 365,
    apiRateLimit: 1000,
    maintenanceMode: false,
    debugMode: false,
    compactMode: false,
    showBalances: true,
    defaultCurrency: countryConfig.primaryCurrency,
    itemsPerPage: 25,
    chartAnimations: true
  });

  const sections = [
    { id: 'general', label: t('settings.general'), icon: SettingsIcon, color: 'from-emerald-500 to-teal-600' },
    { id: 'localization', label: t('settings.localization'), icon: Globe, color: 'from-teal-500 to-cyan-600' },
    { id: 'notifications', label: t('settings.notifications'), icon: Bell, color: 'from-amber-500 to-orange-500' },
    { id: 'security', label: t('settings.security'), icon: Shield, color: 'from-slate-600 to-slate-800' },
    { id: 'system', label: t('settings.system'), icon: Database, color: 'from-emerald-600 to-emerald-700' },
    { id: 'interface', label: t('settings.interface'), icon: Palette, color: 'from-teal-500 to-emerald-600' },
  ];

  const countries = getAvailableCountries();
  const languages = [
    { code: 'fr', name: 'Francais', flag: '🇫🇷' },
    { code: 'en', name: 'English', flag: '🇬🇧' }
  ];

  const themes = [
    { value: 'light', label: t('settings.themeLight') },
    { value: 'dark', label: t('settings.themeDark') },
    { value: 'auto', label: t('settings.themeAuto') }
  ];

  const timezones = [
    'Africa/Kinshasa',
    'Africa/Abidjan',
    'Africa/Accra',
    'Africa/Dakar',
    'Africa/Nairobi',
    'UTC'
  ];

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSaveSettings = () => {
    localStorage.setItem('moko_settings', JSON.stringify(settings));
    if (settings.theme !== theme) setAppTheme(settings.theme as 'light' | 'dark' | 'auto');
    if (settings.language !== language) setLanguage(settings.language as 'fr' | 'en');
    if (settings.country !== country) setCountry(settings.country as any);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleResetSettings = () => {
    if (confirm(t('settings.resetConfirm'))) {
      setSettings({
        theme: 'light', language: 'fr', country: 'CD', timezone: 'Africa/Kinshasa',
        dateFormat: 'DD/MM/YYYY', numberFormat: 'european',
        emailNotifications: true, smsNotifications: true, pushNotifications: true,
        transactionAlerts: true, balanceAlerts: true, securityAlerts: true, marketingEmails: false,
        sessionTimeout: 30, twoFactorAuth: true, loginAlerts: true, ipWhitelist: true, autoLogout: true,
        autoBackup: true, backupFrequency: 'daily', dataRetention: 365, apiRateLimit: 1000,
        maintenanceMode: false, debugMode: false, compactMode: false, showBalances: true,
        defaultCurrency: 'CDF', itemsPerPage: 25, chartAnimations: true
      });
      setAppTheme('light');
    }
  };

  const handleExportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `moko-settings-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const Toggle = ({ checked, onChange }: { checked: boolean; onChange: (val: boolean) => void }) => (
    <button
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${checked ? 'bg-[#63d400]' : 'bg-gray-200'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 shadow-sm ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );

  const SelectField = ({ label, value, onChange, options }: {
    label: string; value: string | number; onChange: (val: string) => void;
    options: { value: string | number; label: string }[];
  }) => (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-[#63d400] focus:border-transparent transition-all duration-200"
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );

  const ToggleRow = ({ label, icon: Icon, checked, onChange }: {
    label: string; icon: any; checked: boolean; onChange: (val: boolean) => void;
  }) => (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-gray-400" />
        <span className="text-sm font-semibold text-gray-900">{label}</span>
      </div>
      <Toggle checked={checked} onChange={onChange} />
    </div>
  );

  const currentSection = sections.find(s => s.id === activeSection) || sections[0];
  const SectionIcon = currentSection.icon;

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SelectField label={t('settings.theme')} value={settings.theme} onChange={(val) => handleSettingChange('theme', val)}
          options={themes.map(th => ({ value: th.value, label: th.label }))} />
        <SelectField label={t('settings.dateFormat')} value={settings.dateFormat} onChange={(val) => handleSettingChange('dateFormat', val)}
          options={[{ value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' }, { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' }, { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' }]} />
        <SelectField label={t('settings.numberFormat')} value={settings.numberFormat} onChange={(val) => handleSettingChange('numberFormat', val)}
          options={[{ value: 'european', label: t('settings.numberEuropean') }, { value: 'american', label: t('settings.numberAmerican') }]} />
        <SelectField label={t('settings.timezone')} value={settings.timezone} onChange={(val) => handleSettingChange('timezone', val)}
          options={timezones.map(tz => ({ value: tz, label: tz }))} />
      </div>
    </div>
  );

  const renderLocalizationSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SelectField label={t('profile.language')} value={settings.language} onChange={(val) => handleSettingChange('language', val)}
          options={languages.map(lang => ({ value: lang.code, label: `${lang.flag} ${lang.name}` }))} />
        <SelectField label={t('country.select')} value={settings.country} onChange={(val) => handleSettingChange('country', val)}
          options={countries.map(c => ({ value: c.code, label: `${c.flag} ${language === 'fr' ? c.nameFr : c.name}` }))} />
        <SelectField label={t('settings.defaultCurrency')} value={settings.defaultCurrency} onChange={(val) => handleSettingChange('defaultCurrency', val)}
          options={countryConfig.currencies.map(c => ({ value: c, label: c }))} />
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-3">
      {[
        { key: 'emailNotifications', label: t('settings.emailNotifications'), icon: Mail },
        { key: 'smsNotifications', label: t('settings.smsNotifications'), icon: Smartphone },
        { key: 'pushNotifications', label: t('settings.pushNotifications'), icon: Bell },
        { key: 'transactionAlerts', label: t('settings.transactionAlerts'), icon: AlertTriangle },
        { key: 'balanceAlerts', label: t('settings.balanceAlerts'), icon: Database },
        { key: 'securityAlerts', label: t('settings.securityAlerts'), icon: Shield },
        { key: 'marketingEmails', label: t('settings.marketingEmails'), icon: Mail }
      ].map(({ key, label, icon }) => (
        <ToggleRow key={key} label={label} icon={icon}
          checked={settings[key as keyof typeof settings] as boolean}
          onChange={(val) => handleSettingChange(key, val)} />
      ))}
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-5">
      <SelectField label={t('settings.sessionTimeout')} value={settings.sessionTimeout}
        onChange={(val) => handleSettingChange('sessionTimeout', parseInt(val))}
        options={[
          { value: 15, label: t('settings.minutes15') }, { value: 30, label: t('settings.minutes30') },
          { value: 60, label: t('settings.hour1') }, { value: 120, label: t('settings.hours2') }, { value: 240, label: t('settings.hours4') },
        ]} />
      <div className="space-y-3">
        {[
          { key: 'twoFactorAuth', label: t('settings.twoFactor'), icon: Key },
          { key: 'loginAlerts', label: t('settings.loginAlerts'), icon: Bell },
          { key: 'ipWhitelist', label: t('settings.ipWhitelist'), icon: Shield },
          { key: 'autoLogout', label: t('settings.autoLogout'), icon: Lock }
        ].map(({ key, label, icon }) => (
          <ToggleRow key={key} label={label} icon={icon}
            checked={settings[key as keyof typeof settings] as boolean}
            onChange={(val) => handleSettingChange(key, val)} />
        ))}
      </div>
    </div>
  );

  const renderSystemSettings = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SelectField label={t('settings.backupFrequency')} value={settings.backupFrequency}
          onChange={(val) => handleSettingChange('backupFrequency', val)}
          options={[
            { value: 'hourly', label: t('settings.hourly') }, { value: 'daily', label: t('settings.daily') },
            { value: 'weekly', label: t('settings.weekly') }, { value: 'monthly', label: t('settings.monthly') },
          ]} />
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.dataRetention')}</label>
          <input type="number" value={settings.dataRetention}
            onChange={(e) => handleSettingChange('dataRetention', parseInt(e.target.value))}
            min="30" max="3650"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-[#63d400] focus:border-transparent" />
        </div>
      </div>
      <div className="space-y-3">
        {[
          { key: 'autoBackup', label: t('settings.autoBackup'), icon: Database },
          { key: 'maintenanceMode', label: t('settings.maintenanceMode'), icon: SettingsIcon },
          { key: 'debugMode', label: t('settings.debugMode'), icon: AlertTriangle }
        ].map(({ key, label, icon }) => (
          <ToggleRow key={key} label={label} icon={icon}
            checked={settings[key as keyof typeof settings] as boolean}
            onChange={(val) => handleSettingChange(key, val)} />
        ))}
      </div>
    </div>
  );

  const renderInterfaceSettings = () => (
    <div className="space-y-5">
      <SelectField label={t('settings.itemsPerPage')} value={settings.itemsPerPage}
        onChange={(val) => handleSettingChange('itemsPerPage', parseInt(val))}
        options={[{ value: 10, label: '10' }, { value: 25, label: '25' }, { value: 50, label: '50' }, { value: 100, label: '100' }]} />
      <div className="space-y-3">
        {[
          { key: 'compactMode', label: t('settings.compactMode'), icon: Palette },
          { key: 'showBalances', label: t('settings.showBalances'), icon: Database },
          { key: 'chartAnimations', label: t('settings.chartAnimations'), icon: Clock }
        ].map(({ key, label, icon }) => (
          <ToggleRow key={key} label={label} icon={icon}
            checked={settings[key as keyof typeof settings] as boolean}
            onChange={(val) => handleSettingChange(key, val)} />
        ))}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'general': return renderGeneralSettings();
      case 'localization': return renderLocalizationSettings();
      case 'notifications': return renderNotificationSettings();
      case 'security': return renderSecuritySettings();
      case 'system': return renderSystemSettings();
      case 'interface': return renderInterfaceSettings();
      default: return renderGeneralSettings();
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-5 sm:p-6 text-white">
        <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        <div className="absolute -top-32 -right-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-[100px]" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-teal-500/15 rounded-full blur-[100px]" />

        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1 mb-3">
              <SettingsIcon className="w-3 h-3 text-emerald-400" />
              <span className="text-[11px] font-semibold text-emerald-300 tracking-wider uppercase">{t('settings.title')}</span>
            </div>
            <h1 className="text-lg sm:text-xl font-bold tracking-tight">{t('settings.title')}</h1>
            <p className="text-sm text-slate-400 mt-1">{t('settings.subtitle')}</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleExportSettings}
              className="flex items-center gap-2 px-4 py-2 bg-white/[0.06] backdrop-blur-sm border border-white/10 rounded-xl text-sm font-medium text-slate-300 hover:bg-white/10 transition-all">
              <Download className="h-4 w-4" />
              {t('common.export')}
            </button>
            <button onClick={handleResetSettings}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-xl text-sm font-medium text-red-300 hover:bg-red-500/20 transition-all">
              <RefreshCw className="h-4 w-4" />
              {t('common.reset')}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="xl:col-span-1">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-3.5 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <SettingsIcon className="h-4 w-4 text-white" />
                </div>
                <h2 className="text-sm font-semibold text-gray-900">Menu</h2>
              </div>
            </div>
            <div className="p-3">
              <nav className="space-y-1">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button key={section.id} onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left rounded-xl text-sm font-medium transition-all duration-200
                        ${activeSection === section.id
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}>
                      <Icon className={`h-4 w-4 ${activeSection === section.id ? 'text-emerald-600' : 'text-gray-400'}`} />
                      {section.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="xl:col-span-3">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${currentSection.color} flex items-center justify-center`}>
                  <SectionIcon className="h-4.5 w-4.5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">{currentSection.label}</h2>
                  <p className="text-xs text-gray-500">{t('settings.configureSection')}</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {renderContent()}

              {/* Save */}
              <div className="mt-8 pt-5 border-t border-gray-100 flex items-center justify-between">
                {saved ? (
                  <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>{t('settings.savedSuccess')}</span>
                  </div>
                ) : <div />}
                <button onClick={handleSaveSettings}
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-2.5 px-6 rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-emerald-500/20 text-sm">
                  <Save className="h-4 w-4" />
                  {t('settings.saveSettings')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System Info */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center">
              <Server className="h-3.5 w-3.5 text-slate-600" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900">{t('settings.systemInfo')}</h3>
          </div>
        </div>
        <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center space-x-3">
            <Monitor className="h-5 w-5 text-gray-400" />
            <div>
              <div className="text-xs text-gray-500">{t('common.version')}</div>
              <div className="text-sm font-semibold text-gray-900">MOKO Afrika v2.1.0</div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            <div>
              <div className="text-xs text-gray-500">{t('common.environment')}</div>
              <div className="text-sm font-semibold text-gray-900">{t('common.production')}</div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Clock className="h-5 w-5 text-gray-400" />
            <div>
              <div className="text-xs text-gray-500">{t('common.lastUpdate')}</div>
              <div className="text-sm font-semibold text-gray-900">{new Date().toLocaleDateString()}</div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <SettingsIcon className="h-5 w-5 text-gray-400" />
            <div>
              <div className="text-xs text-gray-500">{t('common.user')}</div>
              <div className="text-sm font-semibold text-gray-900">{user?.name || user?.username}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
